import {
  EuiButtonGroup,
  EuiLink,
  EuiPageHeaderProps,
  EuiPageTemplate,
  EuiText,
  EuiTitle,
} from "@inscopix/ideas-eui";
import { ButtonCreateContainer } from "components/ButtonCreateContainer/ButtonCreateContainer";
import { ButtonCreateProject } from "components/ButtonCreateProject/ButtonCreateProject";
import { ButtonCreateTool } from "components/ButtonCreateTool/ButtonCreateTool";
import { useRouteMapContext } from "providers/RouteMapProvider/RouteMapProvider";
import { useLocation } from "react-router-dom";

const HeaderDashboardBase = (props: EuiPageHeaderProps) => {
  return (
    <EuiPageTemplate.Header
      restrictWidth={false}
      alignItems="center"
      bottomBorder={false}
      paddingSize="none"
      style={{ padding: 24, paddingBottom: 0 }}
      rightSideGroupProps={{ alignItems: "center" }}
      {...props}
    />
  );
};

const ToolContainerNavButtonGroup = ({
  activeId,
  tenantKey,
}: {
  activeId: "tools" | "containers";
  tenantKey: string;
}) => {
  const toolsRoute = useRouteMapContext((s) => s.routeMap.ORGANIZATION_TOOLS);
  const containersRoute = useRouteMapContext(
    (s) => s.routeMap.ORGANIZATION_CONTAINERS,
  );
  const onChange = (id: "tools" | "containers") => {
    switch (id) {
      case "tools":
        toolsRoute.dynamicPath({ tenantKey }).navigateTo();
        break;
      case "containers":
        containersRoute.dynamicPath({ tenantKey }).navigateTo();
        break;
    }
  };
  return (
    <EuiButtonGroup
      legend="View tools or containers"
      buttonSize="m"
      options={[
        {
          id: "tools" as const,
          label: "Tools",
          value: "Tools",
          iconType: "wrench",
        },
        {
          id: "containers" as const,
          label: "Containers",
          value: "Containers",
          iconType: "package",
        },
      ]}
      idSelected={activeId}
      onChange={(id) => onChange(id as "tools" | "containers")}
    />
  );
};

interface HeaderOrganizationDashboardProps {
  tenantId: number;
  tenantKey: string;
}

export const HeaderOrganizationDashboard = ({
  tenantId,
  tenantKey,
}: HeaderOrganizationDashboardProps) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const location = useLocation();
  const projectRoute = useRouteMapContext((s) => s.routeMap.ORGANIZATION);
  const toolsRoute = useRouteMapContext((s) => s.routeMap.ORGANIZATION_TOOLS);
  const containersRoute = useRouteMapContext(
    (s) => s.routeMap.ORGANIZATION_CONTAINERS,
  );
  const { route, rightSideItems } = (() => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    switch (location.pathname) {
      case toolsRoute.dynamicPath({ tenantKey }).path:
        return {
          route: "tools" as const,
          rightSideItems: [
            <ToolContainerNavButtonGroup
              key="tool-container-nav"
              tenantKey={tenantKey}
              activeId="tools"
            />,
            <ButtonCreateTool key="create-tool" tenantKey={tenantKey} />,
          ],
        };
      case containersRoute.dynamicPath({ tenantKey }).path:
        return {
          route: "containers" as const,
          rightSideItems: [
            <ToolContainerNavButtonGroup
              key="tool-container-nav"
              tenantKey={tenantKey}
              activeId="containers"
            />,
            <ButtonCreateContainer key="create-container" />,
          ],
        };
      default:
        return {
          route: "projects" as const,
          rightSideItems: [
            <ButtonCreateProject key="create-project" tenantId={tenantId} />,
          ],
        };
    }
  })();

  return (
    <HeaderDashboardBase
      tabs={[
        {
          label: (
            <EuiText>
              <EuiLink
                style={{ textDecoration: "none" }}
                color={route === "projects" ? "primary" : "text"}
                type="button"
                to={projectRoute.dynamicPath({ tenantKey }).path}
              >
                <h2>Projects</h2>
              </EuiLink>
            </EuiText>
          ),
          isSelected: route === "projects",
          href: projectRoute.dynamicPath({ tenantKey }).path,
        },
        {
          label: (
            <EuiText>
              <EuiLink
                style={{ textDecoration: "none" }}
                color={
                  route === "tools" || route === "containers"
                    ? "primary"
                    : "text"
                }
                type="button"
                to={toolsRoute.dynamicPath({ tenantKey }).path}
              >
                <h2>Tools</h2>
              </EuiLink>
            </EuiText>
          ),
          isSelected: route === "tools" || route === "containers",
          href: toolsRoute.dynamicPath({ tenantKey }).path,
        },
      ]}
      rightSideGroupProps={{ alignItems: "center" }}
      rightSideItems={rightSideItems}
    />
  );
};

interface HeaderDashboardProps {
  tenantId: number | undefined;
  title?: string;
  showCreateProject?: boolean;
}

export const HeaderDashboard = ({
  tenantId,
  title = "Projects",
  showCreateProject = true,
}: HeaderDashboardProps) => {
  return (
    <HeaderDashboardBase
      pageTitle={
        <EuiTitle>
          <h4>{title}</h4>
        </EuiTitle>
      }
      rightSideItems={
        showCreateProject
          ? [<ButtonCreateProject key="create-project" tenantId={tenantId} />]
          : []
      }
    />
  );
};
